import DottedList from "../../../ui/dottedList";
import { getFacilityTypes } from "../../../../lib/utils";
import ButtonCard from "../../../ui/button/buttonCard";
import BaseStep from "./baseStep";
import useFacilityType from "../../../../api/hooks/use-facility-type";
import useFacilityTypePriceRangeByPolygonsQuery from "../../../../api/hooks/use-facility-type-price-range-by-polygons-query";
import { useEffect, useState } from "react";
import { EMarketTypes } from "../../../../interfaces";
import Loader from "../../../ui/loader";
import clsx from "clsx";
import styles from "./styles.module.scss";
import { ICounterPage } from "./types";

interface RangeByRegionByFacilityTypeStep {
  selectedPolygonIds: number[];
  typeIds: number[];
  numPage?: number;
  counterPage?: ICounterPage;
  onNextStep: () => void;
  onPrevStep: () => void;
  onRepeatStep?: () => void;
}

const RangeByRegionByFacilityTypeStep = ({
  selectedPolygonIds,
  typeIds,
  counterPage,
  onNextStep,
  onPrevStep,
  onRepeatStep,
}: RangeByRegionByFacilityTypeStep) => {
  const [isMount, setIsMount] = useState(false);
  const { facilityTypePriceRangeByPolygonsList, isMutating, trigger } =
    useFacilityTypePriceRangeByPolygonsQuery();

  useEffect(() => {
    trigger({
      facilityTypeIds: typeIds,
      searchPolygonIds: selectedPolygonIds,
      marketType: EMarketTypes.SECONDARY,
    });
    setIsMount(true);
  }, []);

  if (isMutating || !isMount) {
    return (
      <div className={styles.loader}>
        <Loader />
      </div>
    );
  }

  if (!facilityTypePriceRangeByPolygonsList.length) {
    return (
      <div className={styles.errorLoadedData}>
        Ошибка загрузке данных попробуйте позже
      </div>
    );
  }

  return (
    <BaseStep
      counterPage={counterPage}
      yesButtonLabel="Далее"
      noButtonLabel="Добавить планировку"
      yesStep={onNextStep}
      noStep={onRepeatStep}
      isRowYes
      prevStep={onPrevStep}
      text="Проведена оценка на основнии выбраных зон и указанного количества комнат:"
    >
      <div className={styles.rangeByRegionByFacilityTypeStep}>
        {facilityTypePriceRangeByPolygonsList.length && !isMutating && (
          <div className={styles.rangeByRegionByFacilityTypeStep__container}>
            <div className={styles.rangeByRegionByFacilityTypeStep__list}>
              {facilityTypePriceRangeByPolygonsList.map((dataItem) => {
                return <RangeByRegionByFacilityTypeStepItem {...dataItem} />;
              })}
            </div>
          </div>
        )}
      </div>
    </BaseStep>
  );
};

interface RangeByRegionByFacilityTypeStepItem {
  typeId: number;
  listRange: RangeByRegionByFacilityTypeStepItemRange[];
}

const RangeByRegionByFacilityTypeStepItem = ({
  listRange,
  typeId,
}: RangeByRegionByFacilityTypeStepItem) => {
  const { facilityTypeList, isLoading } = useFacilityType();

  const selected = facilityTypeList.find(
    (facilityType) => facilityType.id === typeId
  );

  const transformPlans =
    selected &&
    getFacilityTypes(
      { key: selected.id, label: selected.fullTitle },
      [typeId],
      facilityTypeList,
      false
    );

  if (isLoading) {
    return (
      <div className={styles.loader}>
        <Loader />
      </div>
    );
  }

  if (!facilityTypeList.length) {
    return (
      <div className={styles.errorLoadedData}>
        Ошибка загрузке данных попробуйте позже
      </div>
    );
  }

  return (
    <div
      className={clsx(
        styles.rangeByRegionByFacilityTypeStep__item,
        styles.rangeByRegionByFacilityTypeStepItem
      )}
    >
      <div className={styles.rangeByRegionByFacilityTypeStepItem__container}>
        {facilityTypeList && !isLoading && (
          <div className={styles.rangeByRegionByFacilityTypeStepItem__plan}>
            {transformPlans && (
              <ButtonCard {...transformPlans[0]} isCursorDefault />
            )}
          </div>
        )}
        {facilityTypeList && !isLoading && (
          <div className={styles.rangeByRegionByFacilityTypeStepItem__range}>
            <p className={styles.rangeByRegionByFacilityTypeStepItem__label}>
              Диапазон цен по заданым зонам:
            </p>
            <div
              className={styles.rangeByRegionByFacilityTypeStepItem__rangeList}
            >
              {listRange.map((range) => (
                <RangeByRegionByFacilityTypeStepItemRange {...range} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
interface RangeByRegionByFacilityTypeStepItemRange {
  regionName: string;
  priceFrom: string;
  priceTo: string;
}

const RangeByRegionByFacilityTypeStepItemRange = ({
  regionName,
  priceFrom,
  priceTo,
}: RangeByRegionByFacilityTypeStepItemRange) => {
  const isValidPrice =
    priceTo && priceFrom && priceFrom !== "0" && priceTo !== "0";

  return (
    <div
      className={clsx(
        styles.rangeByRegionByFacilityTypeStepItem__range,
        styles.rangeByRegionByFacilityTypeStepItemRange
      )}
    >
      <div
        className={styles.rangeByRegionByFacilityTypeStepItemRange__container}
      >
        <div
          className={styles.rangeByRegionByFacilityTypeStepItemRange__region}
        >
          {regionName}
        </div>
        {isValidPrice ? (
          <div
            className={styles.rangeByRegionByFacilityTypeStepItemRange__price}
          >
            <div
              className={styles.rangeByRegionByFacilityTypeStepItemRange__from}
            >
              <DottedList items={[{ value: priceFrom, label: "от" }]} />
            </div>
            <div
              className={styles.rangeByRegionByFacilityTypeStepItemRange__to}
            >
              <DottedList items={[{ value: priceTo, label: "до" }]} />
            </div>
          </div>
        ) : (
          <div
            className={styles.rangeByRegionByFacilityTypeStepItemRange__failed}
          >
            {"Не удалось произвести расчет"}
          </div>
        )}
      </div>
    </div>
  );
};

export default RangeByRegionByFacilityTypeStep;
